/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Granite Bagas
*/

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { isWeb, fullWidth } from '@global/screens';
import { parseDate } from '@global/helpers';
import Colors from '@global/colors';
import * as Layout from '@components/screen/Layout';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import SociollaText from '@components/sociolla/SociollaText';
import DatePicker from '@components/form/DatePicker';
import StatusPicker from '@components/form/StatusPicker';
import StatusBadge from '@components/global/StatusBadge';
import Image from '@components/global/Image';
import { showPopup } from '@/store/modal';
import Button from '@components/form/Button';
import SalesTeamSearch from '@components/form/search/SalesTeamSearch';
import { filterAdded, paramsReseted } from '@/store/params';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetInventoryListQuery,
  useGetInventoryCountQuery,
  useGetInventoryInfiniteListInfiniteQuery,
} from '@api/listApi';

const CREATE_ROLES = [
  'superadmin',
  'sales_admin',
  'branch_head',
  'salesman',
  'spv_logistic',
  'general',
];

const tableStructure = {
  salesman_name: {
    head: 'Salesman Name',
    body: 'user.name',
  },
  salesman_code: {
    head: 'Salesman Code',
    body: 'user.sales_code',
  },
  created_date: {
    head: 'Created Date',
    type: 'date',
    body: 'created_at',
  },
  inventory_type: {
    head: 'Inventory Type',
    body: 'inventory_type',
    type: 'slot',
  },
  status: {
    head: 'Status',
    body: 'status',
    type: 'status',
  },
};

// UI filter shape → API query filter (was the inline transform in the old finalParam):
//  - `keyword` unwrapped from the search box's { $regex } object
//  - date range (from_date/to_date) + status pass through
//  - `sales_team` (array of team objects) → sales_team.id: { $in: [id] }
//  - `is_search` set whenever any filter/search is applied (BE flag)
const toApiFilter = (rawFilter = {}) => {
  const filter = {};
  const kw = rawFilter.keyword?.$regex ?? rawFilter.keyword;
  if (kw) {
    filter.keyword = kw;
  }
  if (rawFilter.from_date) {
    filter.from_date = rawFilter.from_date;
  }
  if (rawFilter.to_date) {
    filter.to_date = rawFilter.to_date;
  }
  if (rawFilter.status) {
    filter.status = rawFilter.status;
  }
  if (rawFilter.sales_team?.length) {
    filter['sales_team.id'] = {
      $in: rawFilter.sales_team.map((el) => el.id),
    };
  }
  if (Object.keys(filter).length) {
    filter.is_search = true;
  }
  return filter;
};

const InventoryScreen = (props) => {
  const userData = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const showFilter = useSelector((state) => state.modal.showFilter);

  // ── local state (staged filter draft, seeded on open) ──
  const [draftFilter, setDraftFilter] = useState({});
  const [draftSalesTeam, setDraftSalesTeam] = useState('');

  // ── derived ──
  const statuses = [
    { name: 'Draft', value: 'draft' },
    { name: 'Submitted', value: 'submitted' },
  ];

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetInventoryListQuery,
    countHook: useGetInventoryCountQuery,
    infiniteHook: useGetInventoryInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  // ── memoized values ──
  const showCreateButton = useMemo(
    () => CREATE_ROLES.includes(userData?.role?.code),
    [userData],
  );

  // Current search text, derived from the applied keyword filter.
  const searchValue = useMemo(() => {
    const k = params.filter?.keyword;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  // Web = flat array; mobile = paged infinite result. id is already mapped by the factory.
  const rows = useMemo(() => {
    return isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
  }, [list.data]);

  // ── effects ──
  // Seed the staged filter draft from the applied filter whenever the modal opens.
  useEffect(() => {
    if (showFilter) {
      const applied = params.filter || {};
      setDraftFilter(applied);
      setDraftSalesTeam(applied.sales_team || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilter]);

  // ── handlers ──
  const customSalesTeamFilter = () => {
    if (userData?.sales_team?.length) {
      return {
        _id: { $in: userData.sales_team.map((team) => team.id) },
      };
    } else {
      return {};
    }
  };

  const draftDateHandler = (value, type) => {
    const date = new Date(value);
    setDraftFilter((f) => {
      const next = { ...f };
      if (type === 'from_date') {
        const startDate = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
        );
        next.from_date = { $gte: startDate.toISOString() };
      } else {
        const endDate = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          23,
          59,
          59,
        );
        next.to_date = { $lte: endDate.toISOString() };
      }
      return next;
    });
  };

  const goToDetail = (data) => {
    openChild('InventoryDetailScreen', {
      titleScreen: 'Detail Inventory',
      name: data?.user?.name ? data.user.name : '-',
      id: data.id,
      go_back_key: props.route.key,
    });
  };

  // ── render ──
  return (
    <Layout.Container>
      {isWeb ? (
        <Layout.Title title="List Inventory">
          {showCreateButton ? (
            <Button
              type="primary"
              rounded={true}
              buttonStyle={{ width: 190, marginRight: 10 }}
              title={
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.white }}>
                  + Create New Inventory
                </SociollaText>
              }
              onPress={() => openChild('InventoryFormScreen')}
            />
          ) : (
            false
          )}
        </Layout.Title>
      ) : (
        false
      )}
      {isWeb ? (
        <Table
          filterName={scope}
          name="inventory"
          structures={tableStructure}
          loading={list.isLoading}
          refetching={list.isFetching}
          data={rows}
          searchKey="keyword"
          searchPlaceholder="Search by Salesman name, Salesman code..."
          searchValue={searchValue}
          searchWidth={460}
          hasNoHide={true}
          hasCallback={true}
          totalData={count.data}
          skip={params.skip}
          limit={params.limit}
          onClickRow={({ data }) => goToDetail(data)}
          slot={{
            inventory_type: ({ data, index }) => (
              <SociollaText key={`reference-${index}`}>
                {data?.inventory_type == 'add'
                  ? 'Add Inventory'
                  : 'Return Inventory'}
              </SociollaText>
            ),
          }}
        />
      ) : (
        <TableMobile
          filterName={scope}
          searchKey="keyword"
          placeholder="Search by Salesman name, Salesman code..."
          searchValue={searchValue}
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          data={rows}
          totalData={count.data}
          renderItem={({ data, index }) => (
            <TouchableOpacity
              activeOpacity={0.6}
              style={styles.skpItem}
              onPress={() => goToDetail(data)}
              key={index}>
              <View
                style={[styles.skpItemSection, { alignItems: 'flex-start' }]}>
                <SociollaText
                  type="medium"
                  style={{ fontSize: 15, width: fullWidth - 140 }}>
                  {data.user ? data.user.name : '-'}
                </SociollaText>
                <StatusBadge status={data.status} />
              </View>
              <SociollaText
                style={{
                  marginTop: 12,
                  marginBottom: 10,
                  fontSize: 14,
                  color: Colors.grey2,
                }}>
                {data.user ? data.user.sales_code : '-'}
              </SociollaText>
              <View style={styles.skpItemSection}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Image
                    style={{
                      width: 23,
                      height: 16,
                      marginLeft: -5,
                      marginRight: 5,
                    }}
                    width={23}
                    height={16}
                    svg={true}
                    source={require('@assets/img/icons/menu/calendar.svg')}
                  />
                  <SociollaText style={{ fontSize: 12, color: Colors.grey2 }}>
                    {data.created_at ? parseDate(data.created_at) : '-'}
                  </SociollaText>
                </View>
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.blue3 }}>
                  {data.inventory_type == 'add'
                    ? 'Add Inventory'
                    : 'Return Inventory'}
                </SociollaText>
              </View>
            </TouchableOpacity>
          )}
        />
      )}
      {/* Filter Container */}
      <Layout.Filter
        name="inventory"
        filterName={scope}
        isLocalVariable={true}
        hasFilterData={Object.keys(draftFilter).length > 0}
        onReset={() => {
          if (list.isFetching) {
            return;
          }
          setDraftFilter({});
          setDraftSalesTeam('');
          dispatch(paramsReseted({ key: scope }));
        }}
        onApply={() => {
          if (list.isFetching) {
            return;
          }
          dispatch(filterAdded({ key: scope, filter: draftFilter }));
          dispatch(showPopup({ name: 'showFilter', value: false }));
        }}>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            Created At
          </SociollaText>
          <View style={{ width: '100%', flexDirection: 'row' }}>
            <DatePicker
              label="Start Date"
              name="from_date"
              style={{
                marginRight: isWeb ? 10 : 0,
                width: isWeb ? 'auto' : '50%',
              }}
              disableAfterDate={draftFilter.to_date?.$lte}
              value={draftFilter.from_date?.$gte}
              onChange={(value) => draftDateHandler(value, 'from_date')}
            />
            <DatePicker
              label="End Date"
              name="to_date"
              style={{ width: isWeb ? 'auto' : '50%' }}
              value={draftFilter.to_date?.$lte}
              disableBeforeDate={draftFilter.from_date?.$gte}
              onChange={(value) => draftDateHandler(value, 'to_date')}
            />
          </View>
        </Layout.FilterItem>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            Status
          </SociollaText>
          <StatusPicker
            label=""
            options={statuses}
            value={draftFilter['status']}
            trackBy="value"
            labelKey="name"
            minWidth={80}
            onChange={(val) =>
              setDraftFilter((f) => ({ ...f, status: val.value }))
            }
          />
        </Layout.FilterItem>
        <Layout.FilterItem lastItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            Sales Team
          </SociollaText>
          <SalesTeamSearch
            value={draftSalesTeam}
            placeholder="Select Sales Team"
            multiple={true}
            hideLabel
            customFilter={customSalesTeamFilter()}
            onChange={(value) => {
              setDraftSalesTeam(value);
              setDraftFilter((f) => ({ ...f, sales_team: value }));
            }}
          />
        </Layout.FilterItem>
      </Layout.Filter>
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  skpItem: {
    width: '100%',
    paddingVertical: 20,
    paddingHorizontal: 10,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  skpItemSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: fullWidth - 20,
  },
});

export default InventoryScreen;
